<template>
  <div class="cert-app">
    <!-- Toast -->
    <div class="toast" :class="{ show: toastMsg }">{{ toastMsg }}</div>

    <!-- Overlay -->
    <div class="overlay" :class="{ show: panelOpen }" @click="closeAllPanels"></div>

    <!-- Header -->
    <header class="page-header">
      <span class="brand">互助平台 · 组队认证</span>
      <div class="header-right">
        <span class="sop-btn" @click="showSopPanel = true">?<i v-if="!sopViewed" class="sop-dot"></i></span>
        <span class="username">{{ userName }}</span>
        <button class="logout-btn" @click="logout">退出</button>
      </div>
    </header>

    <!-- Recruitment Banner -->
    <div class="recruit-banner" v-if="recruitConfig.isActive" @click="openRecruitDetail">
      <div class="banner-icon">📣</div>
      <div class="banner-text">
        <div class="banner-title"><span class="pulse-dot"></span>正在招募带领人</div>
        <div class="banner-desc">{{ recruitConfig.content.slice(0, 40) }}{{ recruitConfig.content.length > 40 ? '...' : '' }}</div>
      </div>
      <div class="banner-arrow">›</div>
    </div>

    <!-- Level Tabs -->
    <div class="tab-bar">
      <button :class="{ active: activeTab === 'primary' }" @click="switchTab('primary')">🎓 初级认证</button>
      <button :class="{ active: activeTab === 'level2' }" @click="switchTab('level2')">📚 二级认证</button>
      <button :class="{ active: activeTab === 'mine' }" @click="switchTab('mine')">👤 我的认证小组</button>
    </div>

    <!-- Content: Primary -->
    <div class="content" v-show="activeTab === 'primary'">
      <div class="section-title">🟢 考核冲刺（最长1个月）</div>

      <div v-if="primaryGroups.length === 0" class="empty-stage" @click="openRecruitDetail">
        <span class="empty-icon">🤝</span>
        <span>暂无小组，可报名成为带领人</span>
      </div>
      <div v-for="g in primaryGroups" :key="g.id" class="group-card" @click="openGroupDetail(g.id)">
        <div class="card-header">
          <div class="group-name">{{ g.leader.name }}的小组</div>
          <div class="member-badge" :class="groupStatusBadgeClass(g.status)">👥 {{ g.currentMembers }}/{{ g.maxMembers }} · {{ groupStatusText(g.status) }}</div>
        </div>
        <div class="card-tags">
          <span class="tag" :class="rhythmClass(g.rhythmDesc)">{{ g.rhythmDesc || '复习冲刺' }}</span>
          <span class="tag tag-leader">👤 {{ g.leader.name }}</span>
          <span class="tag tag-period">⏱ {{ g.durationWeeks }}周</span>
          <span class="tag" style="background:#fef5ee;color:#c96d35;" v-if="g.timeSlot">{{ g.timeSlot }}</span>
        </div>
        <div style="font-size:12px;color:#8b7e6a;line-height:1.5;">
          {{ g.startDate }} ~ {{ g.endDate }}
        </div>
        <button class="btn btn-cert btn-sm" style="margin-top:8px;"
          @click.stop="joinGroup(g.id)"
          :disabled="g.currentMembers >= g.maxMembers">
          {{ g.currentMembers >= g.maxMembers ? '已满员' : '加入小组' }}
        </button>
      </div>

      <div class="info-box">
        <strong>📋 什么是初级认证支持？</strong><br>
        聚焦学习期结束后的<strong>1个月考核冲刺</strong>。<br>
        带领人自选周期：最短2周，最长1个月。
      </div>
    </div>

    <!-- Content: Level 2 -->
    <div class="content" v-show="activeTab === 'level2'">
      <!-- Stage Filter -->
      <div class="filter-bar">
        <span class="filter-chip" :class="{ active: l2Filter === 'all' }" @click="l2Filter = 'all'">全部主题</span>
        <span v-for="s in level2Stages" :key="s.key" class="filter-chip"
          :class="{ active: l2Filter === s.key }" @click="l2Filter = s.key">{{ s.shortName }}</span>
      </div>

      <!-- Cohort Filter -->
      <div class="filter-bar" style="margin-top:2px;">
        <span class="filter-chip" :class="{ active: cohortFilter === 'all' }" @click="cohortFilter = 'all'">全部期数</span>
        <span v-for="c in availableCohorts" :key="c" class="filter-chip"
          :class="{ active: cohortFilter === c }" @click="cohortFilter = c">{{ c }}</span>
      </div>

      <div v-if="filteredL2Groups.length === 0" class="empty-stage" @click="openRecruitDetail">
        <span class="empty-icon">🤝</span>
        <span>暂无该阶段的小组，可报名成为带领人</span>
      </div>
      <div v-for="g in filteredL2Groups" :key="g.id" class="group-card" @click="openGroupDetail(g.id)">
        <div class="card-header">
          <div class="group-name">{{ g.leader.name }}的小组</div>
          <div class="member-badge" :class="groupStatusBadgeClass(g.status)">👥 {{ g.currentMembers }}/{{ g.maxMembers }} · {{ groupStatusText(g.status) }}</div>
        </div>
        <div class="card-tags">
          <span class="tag tag-stage">{{ g.certStage }}</span>
          <span v-if="g.cohort" class="tag" style="background:#f4f2fa;color:#8b7ec4;">{{ g.cohort }}</span>
          <span v-if="g.certStage !== '微习惯设计深化'" class="tag" :class="rhythmClass(g.rhythmDesc)">{{ g.rhythmDesc || '复习冲刺' }}</span>
          <span class="tag tag-leader">👤 {{ g.leader.name }}</span>
          <span v-if="g.certStage !== '微习惯设计深化'" class="tag tag-period">⏱ {{ g.durationWeeks }}周</span>
          <span class="tag" style="background:#fef5ee;color:#c96d35;" v-if="g.timeSlot">{{ g.timeSlot }}</span>
        </div>
        <div style="font-size:12px;color:#8b7e6a;line-height:1.5;">
          {{ g.startDate }} ~ {{ g.endDate }}
        </div>
        <button class="btn btn-cert btn-sm" style="margin-top:8px;"
          @click.stop="joinGroup(g.id)"
          :disabled="g.currentMembers >= g.maxMembers">
          {{ g.currentMembers >= g.maxMembers ? '已满员' : '加入小组' }}
        </button>
      </div>

      <div class="info-box">
        <strong>📋 什么是二级认证支持？</strong><br>
        7个月学习期，按3个阶段分别复习冲刺。每个阶段最长1个月。
      </div>
    </div>

    <!-- Content: My Groups -->
    <div class="content" v-show="activeTab === 'mine'">
      <div v-if="loadingMine" class="empty-stage">
        <span class="empty-icon">⏳</span>
        <span>加载中...</span>
      </div>
      <div v-else-if="myCertGroups.length === 0" class="empty-stage" @click="switchTab('primary')">
        <span class="empty-icon">📭</span>
        <span>你还没有加入任何认证小组</span>
        <span style="font-size:11px;color:#b8a99a;">点击浏览初级或二级认证小组</span>
      </div>
      <div v-for="g in myCertGroups" :key="g.id" class="group-card" @click="openGroupDetail(g.id)">
        <div class="card-header">
          <div class="group-name">{{ g.leaderName }}的小组</div>
          <div class="member-badge" :class="groupStatusBadgeClass(g.status)">
            {{ groupStatusText(g.status) }}
          </div>
        </div>
        <div class="card-tags">
          <span class="tag tag-stage">{{ g.certStage }}</span>
          <span v-if="g.cohort" class="tag" style="background:#f4f2fa;color:#8b7ec4;">{{ g.cohort }}</span>
          <span v-if="g.certStage !== '微习惯设计深化'" class="tag tag-period">⏱ {{ g.durationWeeks }}周</span>
          <span class="tag" style="background:#fef5ee;color:#c96d35;" v-if="g.timeSlot">{{ g.timeSlot }}</span>
          <span v-if="g.myRole === 'leader'" class="tag tag-leader">🌟 我是带领人</span>
          <span v-else class="tag" style="background:#eef4fa;color:#4a6d9e;">👤 成员</span>
        </div>
        <div style="font-size:12px;color:#8b7e6a;line-height:1.5;">
          {{ g.startDate }} ~ {{ g.endDate }} · {{ g.currentMembers }}/{{ g.maxMembers }}人
        </div>
        <div v-if="g.status === 'recruiting' || g.status === 'active'" style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap;">
          <button v-if="g.myRole === 'leader' && g.status === 'recruiting'" class="btn btn-sm"
            style="background:#edf6f1;color:#3d7a5a;"
            @click.stop="openCampDatePicker(g.id)">
            ✅ 确认成组，停止招募
          </button>
          <button v-if="g.myRole === 'leader' && g.status === 'active'" class="btn btn-sm"
            style="background:#fef5ee;color:#e8894f;border:1.5px solid #e8894f;"
            @click.stop="openCompleteDialog(g.id)">
            🎉 任务完成
          </button>
          <button v-if="g.myRole === 'leader'" class="btn btn-outline btn-sm"
            style="color:#d4786e;border-color:#d4786e;"
            @click.stop="openDisbandDialog(g.id, true)">
            解散
          </button>
          <button v-else class="btn btn-outline btn-sm"
            style="color:#d4786e;border-color:#d4786e;"
            @click.stop="leaveFromMine(g.id)">
            退出
          </button>
        </div>
        <div v-if="g.myRole === 'leader' && g.status === 'completed'" style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap;">
          <button class="btn btn-sm"
            style="background:#fef5ee;color:#e8894f;"
            @click.stop="doUndoComplete(g.id)">
            ↩ 撤销完成
          </button>
        </div>
        <div v-if="g.myRole === 'leader' && g.status === 'disbanded'" style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap;">
          <button class="btn btn-sm"
            style="background:#fef5ee;color:#e8894f;"
            @click.stop="doUndoDisband(g.id)">
            ↩ 撤销解散
          </button>
          <button class="btn btn-sm"
            style="background:#fff;color:#d4786e;border:1px solid #d4786e;"
            @click.stop="doDeleteGroup(g.id)">
            🗑 删除
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════════════ Recruit Detail Panel ═══════════════ -->
    <div class="panel" :class="{ show: showRecruitPanel }">
      <div class="panel-handle"></div>
      <div class="panel-header">
        <h3>📣 招募带领人</h3>
        <button class="panel-close" @click="closeAllPanels">✕</button>
      </div>
      <div class="panel-body">
        <div class="recruit-detail-header" v-if="!showApplyForm">
          <div class="big-icon">🙋</div>
          <h2>我们在找带领人</h2>
          <div class="deadline" v-if="recruitConfig.deadline">⏰ 截止：{{ recruitConfig.deadline }}</div>
        </div>

        <div v-if="!showApplyForm">
          <div class="recruit-card">
            <h4>🧭 带领人要做什么？</h4>
            <div class="info-row">
              • 发起一个认证阶段的小组，等人加入<br>
              • 每天早上在群里发当日提示语；共读日带领大家一起读文章、碰撞交流<br>
              • 不用盯进度催人教课——内容你都走过，做温柔的领路人就好
            </div>
          </div>

          <div class="recruit-card" v-if="recruitConfig.content">
            <h4>💬 运营说</h4>
            <div class="info-row">{{ recruitConfig.content }}</div>
          </div>

          <!-- Check my applications -->
          <div class="recruit-card" v-if="myApplications.length > 0">
            <h4>📝 我的申请</h4>
            <div v-for="a in myApplications" :key="a.id" class="info-row" style="padding:6px 0;border-bottom:1px solid #f0e8dd;">
              <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:6px;">
                <span>{{ a.certLevel }} · {{ a.certStage }}
                  <span :class="statusBadgeClass(a.status)">{{ statusLabel(a.status) }}</span>
                </span>
                <span v-if="a.status === 'pending'" style="display:flex;gap:6px;">
                  <button class="btn btn-outline btn-sm" style="padding:2px 8px;font-size:11px;" @click.stop="startEditApplication(a)">✏️</button>
                  <button class="btn btn-outline btn-sm" style="padding:2px 8px;font-size:11px;color:#d4786e;border-color:#d4786e;" @click.stop="confirmWithdraw(a.id)">撤回</button>
                </span>
              </div>
            </div>
          </div>

          <button class="btn btn-cert" style="margin-top:12px;" @click="showApplyForm = true">
            🙋 我要报名成为带领人
          </button>
        </div>

        <!-- Apply/Edit Form -->
        <div v-if="showApplyForm || editingAppId > 0" class="apply-form-section">
          <button class="btn btn-outline btn-sm" style="margin-bottom:12px;" @click="editingAppId > 0 ? cancelEditApplication() : (showApplyForm = false)">← 返回</button>

          <div class="recruit-card">
            <h4 v-if="editingAppId > 0">修改申请</h4>
            <h4 v-else>选择认证阶段</h4>
            <div class="stage-chips">
              <template v-if="editingAppId > 0">
                <span v-for="s in applyStages" :key="s.key" class="stage-chip"
                  :class="{ selected: editStage === s.key }"
                  @click="editStage = s.key">{{ s.label }}</span>
              </template>
              <template v-else>
                <span v-for="s in applyStages" :key="s.key" class="stage-chip"
                  :class="{ selected: applyStage === s.key }"
                  @click="applyStage = s.key">{{ s.label }}</span>
              </template>
            </div>
            <div class="hint" v-if="editingAppId > 0 ? !editStage : !applyStage">请选择一个阶段</div>
          </div>

          <div class="form-group">
            <label>想说的话 <span class="hint-text">（选填）</span></label>
            <textarea v-if="editingAppId > 0" v-model="editMessage" placeholder="比如你目前在哪个进度、希望什么样的节奏..."></textarea>
            <textarea v-else v-model="applyMessage" placeholder="比如你目前在哪个进度、希望什么样的节奏..."></textarea>
            <div class="hint-text">不写也完全可以——提交后运营会跟你私聊沟通</div>
          </div>

          <button v-if="editingAppId > 0" class="btn btn-cert" @click="saveEditApplication">
            💾 保存修改
          </button>
          <button v-else class="btn btn-cert" @click="submitApply" :disabled="applying">
            {{ applying ? '提交中...' : '📩 提交申请' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════════════ Group Detail Panel ═══════════════ -->
    <div class="panel" :class="{ show: showDetailPanel }">
      <div class="panel-handle"></div>
      <div class="panel-header">
        <h3>{{ detail ? detail.leader.name + '的小组' : '小组详情' }}</h3>
        <button class="panel-close" @click="closeAllPanels">✕</button>
      </div>
      <div class="panel-body" v-if="detail">
        <div class="recruit-card">
          <h4>📌 {{ detail.certStage }}</h4>
          <div style="margin-top:8px;">
            <span v-if="detail.cohort" class="tag" style="background:#f4f2fa;color:#8b7ec4;">{{ detail.cohort }}</span>
            <span v-if="detail.certStage !== '微习惯设计深化'" class="tag" :class="rhythmClass(detail.rhythmDesc)">{{ detail.rhythmDesc || '复习冲刺' }}</span>
            <span class="tag tag-leader">👤 {{ detail.leader.name }}</span>
            <span v-if="detail.certStage !== '微习惯设计深化'" class="tag tag-period">⏱ {{ detail.durationWeeks }}周</span>
          </div>
          <div style="font-size:12px;color:#8b7e6a;margin-top:6px;">{{ detail.startDate }} ~ {{ detail.endDate }}</div>
        </div>

        <div class="recruit-card" v-if="detail.description">
          <h4>📝 小组介绍</h4>
          <div style="font-size:14px;color:#8b7e6a;line-height:1.8;">{{ detail.description }}</div>
        </div>

        <div class="recruit-card">
          <h4>👥 当前成员（{{ detail.currentMembers }}/{{ detail.maxMembers }}）</h4>
          <div style="font-size:13px;color:#8b7e6a;line-height:2;">
            <div v-for="(m, i) in detail.members" :key="i" class="member-row">
              <span class="member-avatar">{{ m.initial }}</span>
              <strong v-if="m.role === 'leader'">{{ m.name }}</strong>
              <span v-else>{{ m.name }}</span>
              <span v-if="m.role === 'leader'" style="font-size:11px;color:#b8a99a;">· 带领人</span>
              <span v-if="m.wechat" class="wechat-info">
                微信号：{{ m.wechat }}
                <button class="copy-btn" @click="copyWechat(m.wechat)">📋</button>
              </span>
            </div>
          </div>
        </div>

        <!-- ═══════════════ Checklist Tracking Card (Leader Only) ═══════════════ -->
        <div class="recruit-card" v-if="detail.status === 'active' && detail.campStartDate && detail.leader.name === userName && checklistStatus && (checklistStatus.survey || checklistStatus.confirm)" style="margin-top:12px;">
          <h4>📋 组员完成追踪</h4>

          <!-- Loading -->
          <div v-if="checklistLoading" style="text-align:center;padding:12px;color:#b8a99a;">加载中...</div>

          <!-- Content -->
          <template v-else>
            <div style="font-size:12px;color:#8b7e6a;margin-bottom:10px;">当前：Day {{ checklistStatus.dayNumber }}</div>

            <!-- Survey section (D1-D3) -->
            <div v-if="checklistStatus.survey" style="background:#fef9f2;border-radius:8px;padding:12px;margin-bottom:10px;">
              <div style="font-size:13px;font-weight:600;margin-bottom:8px;">📋 D1 调研 — 填写窗口：D1 ~ D3</div>
              <button class="btn btn-cert" style="width:100%;justify-content:center;padding:10px;font-size:13px;"
                @click="copyChecklistLink('survey')">
                🔗 复制调研链接，发到微信群
              </button>
            </div>

            <!-- Confirm section (D28-D35) -->
            <div v-if="checklistStatus.confirm" style="background:#f0f4ff;border-radius:8px;padding:12px;margin-bottom:10px;">
              <div style="font-size:13px;font-weight:600;margin-bottom:8px;">📋 D28 确认 — 填写窗口：D28 ~ D35（含一周后置时间）</div>
              <button class="btn btn-cert" style="width:100%;justify-content:center;padding:10px;font-size:13px;background:linear-gradient(135deg,#8b7ec4,#6b5ea4);"
                @click="copyChecklistLink('confirm')">
                🔗 复制确认链接，发到微信群
              </button>
            </div>

            <!-- Member status table -->
            <div v-if="checklistStatus.members && checklistStatus.members.length" style="margin-top:8px;">
              <div v-for="m in checklistStatus.members" :key="m.userName"
                   style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid #f5f0eb;font-size:12px;">
                <span style="font-weight:500;min-width:50px;">{{ m.userName }}</span>
                <span v-if="checklistStatus.survey" :style="{color: m.hasSubmittedSurvey ? '#3d7a5a' : '#d4786e'}">
                  {{ m.hasSubmittedSurvey ? '✅' : '○' }} 调研
                </span>
                <span v-if="checklistStatus.confirm" :style="{color: m.hasSubmittedConfirm ? '#3d7a5a' : '#d4786e'}">
                  {{ m.hasSubmittedConfirm ? '✅' : '○' }} 确认
                </span>
                <span v-if="!m.hasSubmittedSurvey && !m.hasSubmittedConfirm" style="font-size:10px;color:#d4786e;">未填写</span>
              </div>
            </div>

            <!-- No members -->
            <div v-else style="text-align:center;padding:8px;color:#b8a99a;font-size:12px;">暂无成员数据</div>
          </template>
        </div>

        <button v-if="!isMyGroup && detail.currentMembers < detail.maxMembers"
          class="btn btn-cert" style="margin-top:12px;" @click="joinGroup(detail.id)">
          🙋 加入这个小组
        </button>
        <button v-if="!isMyGroup && detail.currentMembers >= detail.maxMembers"
          class="btn btn-cert" style="margin-top:12px;opacity:0.5;" disabled>
          已满员
        </button>
        <button v-if="isMyGroup && detail.leader.name !== userName"
          class="btn btn-outline" style="margin-top:12px;color:#d4786e;border-color:#d4786e;"
          @click="leaveGroup(detail.id)">
          退出小组
        </button>
        <button v-if="detail.leader.name === userName && detail.status === 'recruiting'"
          class="btn btn-cert" style="margin-top:12px;background:#edf6f1;color:#3d7a5a;"
          @click="openCampDatePicker(detail.id)">
          ✅ 确认成组，停止招募
        </button>
        <div v-if="detail.leader.name === userName" style="display:flex;gap:10px;margin-top:12px;">
          <button v-if="detail.status === 'active'"
            class="btn btn-cert" style="flex:1;background:#fef5ee;color:#e8894f;border:1.5px solid #e8894f;"
            @click="openCompleteDialog(detail.id)">
            🎉 任务完成
          </button>
          <button class="btn btn-outline" style="flex:1;color:#d4786e;border-color:#d4786e;"
            @click="openDisbandDialog(detail.id, false)">
            解散小组
          </button>
        </div>
        <button v-if="detail.leader.name === userName && detail.status === 'completed'"
          class="btn btn-cert" style="margin-top:12px;background:#fef5ee;color:#e8894f;"
          @click="doUndoComplete(detail.id)">
          ↩ 撤销完成
        </button>
        <button v-if="detail.leader.name === userName && detail.status === 'disbanded'"
          class="btn btn-cert" style="margin-top:12px;background:#fef5ee;color:#e8894f;"
          @click="doUndoDisband(detail.id)">
          ↩ 撤销解散
        </button>
        <div v-if="detail.status === 'active' && detail.campStartDate" style="display:flex;gap:10px;margin-top:12px;">
          <button v-if="detail.leader.name === userName" class="btn btn-cert" style="flex:1;"
            @click="openDailySop(detail)">
            📋 每日提示语
          </button>
          <button v-if="detail.leader.name === userName" class="btn btn-cert" style="flex:1;background:linear-gradient(135deg,#8b7ec4,#6b5ea4);"
            @click="openLeaderSop(detail)">
            📖 组长运营包
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════════════ Camp Date Picker ═══════════════ -->
    <div class="overlay" v-if="showCampDatePicker" @click.self="showCampDatePicker=false"></div>
    <div class="dialog" v-if="showCampDatePicker">
      <h3 style="margin-bottom:12px;">📅 选择开营日期</h3>
      <p style="font-size:13px;color:#8b7e6a;margin-bottom:12px;">Day 1 从这天开始算。建议选周一。</p>
      <input type="date" v-model="campDate" class="input" style="margin-bottom:16px;" />
      <div style="display:flex;gap:10px;">
        <button class="btn-cancel" @click="showCampDatePicker=false" style="flex:1;">取消</button>
        <button class="btn-save" @click="confirmWithDate" :disabled="confirming" style="flex:1;">{{ confirming ? "确认中..." : "确认开营" }}</button>
      </div>
    </div>

    <!-- ═══════════════ Daily SOP Panel ═══════════════ -->
    <div class="overlay" v-if="showDailySop" @click.self="showDailySop=false"></div>
    <div class="panel" :class="{ show: showDailySop }" style="max-height:90vh;overflow-y:auto;">
      <div class="panel-handle"></div>
      <div style="display:flex;justify-content:space-between;align-items:center;padding:0 4px;">
        <h3 style="margin:0;">📋 每日提示语</h3>
        <button class="panel-close" @click="showDailySop=false">✕</button>
      </div>
      <div v-if="dailySopData" style="margin-top:16px;">
        <!-- Week tabs -->
        <div style="display:flex;gap:6px;margin-bottom:16px;overflow-x:auto;">
          <button v-for="(w, i) in dailySopData.weeks" :key="i"
            :class="['chip', { active: dailySopWeek === i }]"
            @click="dailySopWeek = i; dailySopDay = w.days[0].day; dailySopCopied = false"
            style="white-space:nowrap;flex-shrink:0;">
            Week {{ w.week }}
          </button>
        </div>
        <!-- Current week's days -->
        <div style="display:flex;gap:6px;margin-bottom:14px;overflow-x:auto;padding-bottom:4px;">
          <button v-for="d in dailySopData.weeks[dailySopWeek]?.days || []" :key="d.day"
            :class="['chip', { active: dailySopDay === d.day }]"
            @click="dailySopDay = d.day; dailySopCopied = false"
            style="white-space:nowrap;flex-shrink:0;">
            {{ d.label }}
          </button>
        </div>
        <!-- Day content -->
        <div v-if="dailySopData.weeks[dailySopWeek]" style="background:#fef9f2;border-radius:12px;padding:16px;">
          <template v-for="d in dailySopData.weeks[dailySopWeek].days" :key="d.day">
            <div v-if="d.day === dailySopDay">
              <div style="font-size:13px;color:#e8894f;font-weight:600;margin-bottom:4px;">{{ d.tag }}</div>
              <div style="font-size:14px;color:#3d3529;white-space:pre-wrap;line-height:1.6;" v-html="renderSopContent(d.content)"></div>
            </div>
          </template>
        </div>
        <!-- Copy button + nav -->
        <div style="display:flex;gap:10px;margin-top:14px;align-items:center;">
          <button @click="goSopDay(-1)" :disabled="dailySopDay <= 1"
            style="padding:8px 14px;border:1px solid #e8e4df;border-radius:8px;background:#fff;cursor:pointer;">← 前一天</button>
          <button @click="goSopDay(1)" :disabled="dailySopDay >= 28"
            style="padding:8px 14px;border:1px solid #e8e4df;border-radius:8px;background:#fff;cursor:pointer;">后一天 →</button>
          <button @click="copySopContent"
            style="margin-left:auto;padding:8px 18px;border:none;border-radius:8px;background:#e8894f;color:#fff;font-weight:600;cursor:pointer;">
            {{ dailySopCopied ? '✅ 已复制' : '📋 一键复制' }}
          </button>
        </div>
      </div>
      <div v-else style="text-align:center;padding:20px;color:#b8a99a;">
        该认证阶段暂无每日提示语
      </div>
    </div>

    <!-- ═══════════════ Create Cert Group Panel ═══════════════ -->
    <div class="panel" :class="{ show: showCreatePanel }">
      <div class="panel-handle"></div>
      <div class="panel-header">
        <h3>发起认证小组</h3>
        <button class="panel-close" @click="closeAllPanels">✕</button>
      </div>
      <div class="panel-body">
        <div class="form-group">
          <label>认证等级</label>
          <div style="display:flex;gap:6px;flex-wrap:wrap;">
            <span v-for="l in ['初级','二级']" :key="l" class="stage-chip"
              :class="{ selected: createLevel === l }"
              @click="createLevel = l">{{ l === '初级' ? '初级 · 考核冲刺' : '二级' }}</span>
          </div>
        </div>
        <div class="form-group" v-if="createLevel === '二级'">
          <label>认证期数</label>
          <div style="display:flex;gap:6px;flex-wrap:wrap;">
            <span v-for="c in ['1-5期','6期','不限']" :key="c" class="stage-chip"
              :class="{ selected: createCohort === c }"
              @click="createCohort = c">{{ c }}</span>
          </div>
        </div>
        <div class="form-group" v-if="createLevel === '二级'">
          <label>主题</label>
          <div style="display:flex;gap:6px;flex-wrap:wrap;">
            <span v-for="s in ['觉察觉照深化','倾听与提问','微习惯设计深化']" :key="s" class="stage-chip"
              :class="{ selected: createStage === s }"
              @click="createStage = s">{{ s }}</span>
          </div>
        </div>
        <div class="form-group" v-if="createLevel !== '二级'">
          <label>周期</label>
          <select v-model.number="createDuration">
            <option :value="0">请选择</option>
            <option :value="2">2 周</option>
            <option :value="3">3 周</option>
            <option :value="4">1 个月（4周）</option>
          </select>
        </div>
        <div class="form-group" v-if="createLevel !== '二级'">
          <label>进度节奏</label>
          <input type="text" v-model="createRhythm" placeholder="如：阶段一刚学完，从头复习" />
        </div>
        <div class="form-group">
          <label>人数上限（{{ createMinMembers }}-8人）</label>
          <select v-model.number="createMaxMembers">
            <option v-for="n in createMemberOptions" :key="n" :value="n">{{ n }} 人</option>
          </select>
        </div>
        <div class="form-group">
          <label>共读时间段</label>
          <div style="display:flex;gap:6px;flex-wrap:wrap;">
            <span v-for="t in timeSlots" :key="t" class="stage-chip"
              :class="{ selected: createTimeSlot === t }"
              @click="createTimeSlot = t">{{ t }}</span>
          </div>
        </div>
        <div class="form-group">
          <label>小组简介（最长200字）</label>
          <textarea v-model="createDesc" maxlength="200" placeholder="介绍一下你的小组..."></textarea>
        </div>

        <button class="btn btn-cert" @click="doCreateGroup" :disabled="creating">
          {{ creating ? '创建中...' : '🚀 发起认证小组' }}
        </button>
      </div>
    </div>

    <!-- ═══════════════ SOP Panel ═══════════════ -->
    <div class="panel" :class="{ show: showSopPanel }">
      <div class="panel-handle"></div>
      <div class="panel-header">
        <h3>📖 操作指南</h3>
        <button class="panel-close" @click="showSopPanel = false; sopViewed = true; saveSopViewed()">✕</button>
      </div>
      <div class="panel-body">
        <div class="tab-bar" style="padding:0;">
          <button :class="{ active: sopTab === 'leader' }" @click="sopTab = 'leader'">🌟 带领人</button>
          <button :class="{ active: sopTab === 'participant' }" @click="sopTab = 'participant'">👤 参与人</button>
        </div>
        <div v-if="sopTab === 'leader'" style="padding-top:16px;">
          <div class="recruit-card" v-for="(step, i) in leaderSop" :key="i">
            <h4>{{ i + 1 }}. {{ step.title }}</h4>
            <div class="info-row">{{ step.desc }}</div>
          </div>
        </div>
        <div v-if="sopTab === 'participant'" style="padding-top:16px;">
          <div class="recruit-card" v-for="(step, i) in participantSop" :key="i">
            <h4>{{ i + 1 }}. {{ step.title }}</h4>
            <div class="info-row">{{ step.desc }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════════ Complete Feedback Dialog ═══════════════ -->
    <div class="overlay" v-if="showCompleteDialog" @click.self="showCompleteDialog=false"></div>
    <div class="dialog" v-if="showCompleteDialog">
      <h3 style="margin-bottom:8px;">🎉 标记本周期已完成</h3>
      <p style="font-size:13px;color:#8b7e6a;margin-bottom:12px;">完成后小组将关闭。有什么想对运营说的？（选填）</p>
      <textarea v-model="completeFeedback" rows="3" style="width:100%;padding:10px;border:1px solid #e8e4df;border-radius:8px;font-size:13px;font-family:inherit;resize:vertical;" placeholder="比如：这次带组有什么收获或建议..."></textarea>
      <div style="display:flex;gap:10px;margin-top:14px;">
        <button class="btn-cancel" @click="showCompleteDialog=false" style="flex:1;">取消</button>
        <button class="btn-save" @click="doCompleteWithFeedback" :disabled="completing" style="flex:1;">{{ completing ? '提交中...' : '确认完成' }}</button>
      </div>
    </div>

    <!-- ═══════════════ Disband Confirmation Dialog ═══════════════ -->
    <div class="overlay" v-if="showDisbandDialog" @click.self="showDisbandDialog=false"></div>
    <div class="dialog" v-if="showDisbandDialog">
      <h3 style="margin-bottom:8px;">⚠️ 确定要解散小组吗？</h3>
      <p style="font-size:13px;color:#8b7e6a;margin-bottom:12px;">解散后所有成员将无法再看到这个小组。24小时内可撤销。</p>
      <div style="display:flex;gap:10px;">
        <button class="btn-cancel" @click="showDisbandDialog=false" style="flex:1;">取消</button>
        <button class="btn-save" @click="doDisbandConfirm" :disabled="disbanding" style="flex:1;background:#d4786e;">{{ disbanding ? '解散中...' : '确认解散' }}</button>
      </div>
    </div>

    <!-- ═══════════════ Leader SOP Panel ═══════════════ -->
    <div class="overlay" v-if="showLeaderSop" @click.self="showLeaderSop=false"></div>
    <div class="panel" :class="{ show: showLeaderSop }" style="max-height:90vh;overflow-y:auto;">
      <div class="panel-handle"></div>
      <div style="display:flex;justify-content:space-between;align-items:center;padding:0 4px;">
        <h3 style="margin:0;">📖 组长运营包</h3>
        <button class="panel-close" @click="showLeaderSop=false">✕</button>
      </div>
      <div v-if="leaderSopData" style="margin-top:16px;">
        <div class="recruit-card" v-if="leaderSopData.leaderNotes && leaderSopData.leaderNotes.length">
          <h4>📌 给组长的说明</h4>
          <div class="info-row" v-for="(note, i) in leaderSopData.leaderNotes" :key="i" style="margin-bottom:6px;">{{ note }}</div>
        </div>
        <div class="recruit-card" v-if="leaderSopData.requirements">
          <h4>✅ 必选 vs 可选</h4>
          <div class="info-row"><strong>必选：</strong>{{ leaderSopData.requirements.required }}</div>
          <div class="info-row" style="margin-top:4px;"><strong>可选：</strong>{{ leaderSopData.requirements.optional }}</div>
        </div>
        <div class="recruit-card" v-if="leaderSopData.readingPath && leaderSopData.readingPath.length">
          <h4>📚 共读流程及案例记录路径</h4>
          <div class="info-row" v-for="(p, i) in leaderSopData.readingPath" :key="i" style="margin-bottom:4px;">• {{ p }}</div>
        </div>
      </div>
      <div v-else style="text-align:center;padding:20px;color:#b8a99a;">该认证阶段暂无组长运营包</div>
    </div>

    <!-- FAB: Create group (only visible if user is an approved leader) -->
    <button v-if="isApprovedLeader" class="fab" @click="openCreatePanel">
      <span class="fab-icon">+</span>
      <span class="fab-text">创建</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import {
  fetchRecruitConfig, applyForLeader, fetchMyLeaderStatus,
  fetchCertGroups, fetchCertGroupDetail, fetchMyCertGroups,
  createCertGroup, joinCertGroup, leaveCertGroup, disbandCertGroup,
  confirmCertGroup, completeCertGroup, undoCompleteCertGroup, undoDisbandCertGroup, deleteCertGroup,
  editLeaderApplication, withdrawLeaderApplication, fetchLeaderSop
} from "../services/cert-api";

const router = useRouter();

// ═══════════════ User ═══════════════
const rawUser = localStorage.getItem("rc_user");
const user = rawUser ? JSON.parse(rawUser) : { name: "教练", stage: "未知" };
const userName = user.name;

// ═══════════════ State ═══════════════
const activeTab = ref("primary");
const toastMsg = ref("");
const panelOpen = computed(() => showRecruitPanel.value || showDetailPanel.value || showCreatePanel.value);

// Recruit
const recruitConfig = ref<any>({ isActive: false, content: "", stages: [], deadline: null });
const showRecruitPanel = ref(false);
const showApplyForm = ref(false);
const applyStage = ref("");
const applyMessage = ref("");
const applying = ref(false);
const myApplications = ref<any[]>([]);

// Groups
const allGroups = ref<any[]>([]);
const showDetailPanel = ref(false);
const showCreatePanel = ref(false);
const detail = ref<any>(null);
const creating = ref(false);
const l2Filter = ref('all');
const cohortFilter = ref('all');
const timeSlots = ["早上","上午","下午","晚上","灵活不固定"];

// SOP
const showSopPanel = ref(false);
const sopTab = ref("leader");
const sopViewed = ref(localStorage.getItem("cert_sop_viewed") === "1");
function saveSopViewed() { localStorage.setItem("cert_sop_viewed", "1"); }
const leaderSop = [
  { title: "发起小组", desc: "选择认证阶段、周期和时间段，填写小组简介。创建后状态为「招募中」。" },
  { title: "确认成组", desc: "等成员加入后，点击「确认成组，停止招募」，小组状态变为「进行中」。" },
  { title: "添加微信 · 建群", desc: "在小组详情页复制每位成员的微信号，添加好友，建微信群。在群里打招呼，发第一次共读时间。" },
  { title: "按节奏带共读", desc: "你不是老师，你只是一个先走一步的人。按承诺的节奏带共读/共练，每次结束后简单总结发群里。" },
  { title: "周期结束", desc: "周期结束后，点击「本周期已完成」，填写一句想对运营说的话（选填），小组状态变为「已完成」。感谢你的带领 🙏" },
];
const participantSop = [
  { title: "找到合适的小组", desc: "浏览认证广场，看阶段是否匹配、时间段是否合适、节奏描述是否符合你的习惯。" },
  { title: "加入小组", desc: "点击「加入小组」按钮即可加入。满员后无法加入。" },
  { title: "联系带领人 · 进群", desc: "加入后，在小组详情页找到带领人的微信号，主动添加好友。提醒带领人建群。" },
  { title: "全程参与", desc: "按时参加共读/共练。你不是来上课的——你是一起走的人。参与越多，收获越多。" },
];

// ── Daily SOP (每日提示语) ──
const showDailySop = ref(false);
const dailySopData = ref<any>(null);
const dailySopDay = ref(1);
const dailySopWeek = ref(0);
const dailySopCopied = ref(false);

// ── Leader SOP (组长运营包) ──
const showLeaderSop = ref(false);
const leaderSopData = ref<any>(null);

// ── Checklist Tracking (Leader) ──
const checklistStatus = ref<any>(null);
const checklistLoading = ref(false);

// ── Confirmation dialogs ──
const showCompleteDialog = ref(false);
const completeFeedback = ref("");
const pendingCompleteId = ref(0);
const completing = ref(false);
const showDisbandDialog = ref(false);
const pendingDisbandId = ref(0);
const disbanding = ref(false);
const isFromMine = ref(false);

function nextMonday(): string {
  const d = new Date();
  d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
  return d.toISOString().slice(0, 10);
}

function calcSopDay(campStartDate: string): number {
  if (!campStartDate) return 1;
  const start = new Date(campStartDate);
  const today = new Date();
  const diff = Math.floor((today.getTime() - start.getTime()) / 86400000);
  return Math.max(1, Math.min(28, diff + 1));
}

async function openDailySop(group: any) {
  dailySopDay.value = calcSopDay(group.campStartDate || "");
  dailySopWeek.value = Math.floor((dailySopDay.value - 1) / 7);
  dailySopCopied.value = false;
  if (!dailySopData.value) {
    try {
      const res = await fetch(`/api/cert/sop/${encodeURIComponent(group.certStage || "微习惯设计深化")}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("rc_token") ?? ""}` }
      });
      dailySopData.value = await res.json();
    } catch { dailySopData.value = null; }
  }
  showDailySop.value = true;
}

function renderSopContent(text: string): string {
  if (!text) return '';
  // Wrap #小程序:// links with styled span for visual display
  return text.replace(/(#小程序:\/\/\S+)/g,
    '<span style="color:#8b7ec4;background:#f4f2fa;padding:1px 5px;border-radius:3px;font-size:12px;white-space:nowrap;">📱 $1</span>');
}

function copySopContent() {
  if (!dailySopData.value) return;
  const week = dailySopData.value.weeks[dailySopWeek.value];
  if (!week) return;
  const day = week.days.find((d: any) => d.day === dailySopDay.value);
  if (!day) return;
  const text = `${day.label}\n\n${day.content}`;
  navigator.clipboard.writeText(text).then(() => {
    dailySopCopied.value = true;
    setTimeout(() => dailySopCopied.value = false, 2000);
  }).catch(() => {});
}

function goSopDay(delta: number) {
  const newDay = dailySopDay.value + delta;
  if (newDay < 1 || newDay > 28) return;
  dailySopDay.value = newDay;
  dailySopWeek.value = Math.floor((newDay - 1) / 7);
  dailySopCopied.value = false;
}

// ── Leader SOP (组长运营包) ──
async function openLeaderSop(group: any) {
  if (!leaderSopData.value) {
    try {
      leaderSopData.value = await fetchLeaderSop(group.certStage || "微习惯设计深化");
    } catch { leaderSopData.value = null; }
  }
  showLeaderSop.value = true;
}

// ── Checklist Tracking (Leader) ──
async function fetchChecklistStatus(groupId: number) {
  checklistLoading.value = true;
  try {
    const res = await fetch(`/api/checklist/${groupId}/status`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("rc_token") ?? ""}` }
    });
    checklistStatus.value = await res.json();
  } catch { checklistStatus.value = null; }
  checklistLoading.value = false;
}

function checklistLink(type: string): string {
  const session = type === 'survey' ? checklistStatus.value?.survey : checklistStatus.value?.confirm;
  if (!session) return '';
  return `${window.location.origin}/checklist/${session.token}`;
}

function copyChecklistLink(type: string) {
  const link = checklistLink(type);
  const title = detail.value?.certStage || '微习惯学习';
  let text: string;
  if (type === 'survey') {
    text = `📋 ${title} — 学习进度调研\n请点击链接，勾选你目前还没有完成的项目：\n${link}`;
  } else {
    text = `📋 ${title} — 学习进度确认\n请点击链接，确认你各项任务的完成情况：\n${link}`;
  }
  navigator.clipboard.writeText(text).then(() => {
    toastMsg.value = '✅ 链接已复制（含说明文字）';
    setTimeout(() => toastMsg.value = '', 1500);
  }).catch(() => {});
}

// ── Complete with custom dialog ──
function openCompleteDialog(groupId: number) {
  closeAllPanels();
  pendingCompleteId.value = groupId;
  completeFeedback.value = "";
  showCompleteDialog.value = true;
}

async function doCompleteWithFeedback() {
  if (completing.value) return;
  completing.value = true;
  const fb = completeFeedback.value.trim() || undefined;
  try {
    const r = await completeCertGroup(pendingCompleteId.value, fb);
    if (r.error) { toast(r.error); completing.value = false; return; }
    toast("🎉 任务完成！");
    showCompleteDialog.value = false;
    closeAllPanels();
    await Promise.all([loadGroups(), loadMyCertGroupsData()]);
  } catch { toast("操作失败"); }
  completing.value = false;
}

// ── Disband with custom dialog ──
function openDisbandDialog(groupId: number, fromMine: boolean) {
  closeAllPanels();
  pendingDisbandId.value = groupId;
  isFromMine.value = fromMine;
  showDisbandDialog.value = true;
}

async function doDisbandConfirm() {
  if (disbanding.value) return;
  disbanding.value = true;
  try {
    const r = await disbandCertGroup(pendingDisbandId.value);
    if (r.error) { toast(r.error); disbanding.value = false; return; }
    toast("小组已解散");
    showDisbandDialog.value = false;
    closeAllPanels();
    await Promise.all([loadGroups(), loadMyCertGroupsData()]);
  } catch { toast("操作失败"); }
  disbanding.value = false;
}

// ── Undo complete/disband ──
async function doUndoComplete(groupId: number) {
  try {
    const r = await undoCompleteCertGroup(groupId);
    if (r.error) { toast(r.error); return; }
    toast("✅ 已撤销完成，小组恢复进行中");
    await Promise.all([loadGroups(), loadMyCertGroupsData()]);
  } catch { toast("操作失败"); }
}

async function doUndoDisband(groupId: number) {
  try {
    const r = await undoDisbandCertGroup(groupId);
    if (r.error) { toast(r.error); return; }
    toast("✅ 已撤销解散，小组恢复招募中");
    await Promise.all([loadGroups(), loadMyCertGroupsData()]);
  } catch { toast("操作失败"); }
}

async function doDeleteGroup(groupId: number) {
  if (!confirm("确定要永久删除该小组吗？此操作不可撤销。")) return;
  try {
    const r = await deleteCertGroup(groupId);
    if (r.error) { toast(r.error); return; }
    toast("🗑 小组已删除");
    await Promise.all([loadGroups(), loadMyCertGroupsData()]);
    closeAllPanels();
  } catch { toast("操作失败"); }
}

// ── Camp date picker for confirm ──
const showCampDatePicker = ref(false);
const campDate = ref("");
const pendingConfirmGroupId = ref(0);
const confirming = ref(false);

function openCampDatePicker(groupId: number) {
  pendingConfirmGroupId.value = groupId;
  campDate.value = nextMonday();
  showCampDatePicker.value = true;
}

async function confirmWithDate() {
  if (confirming.value) return;
  confirming.value = true;
  const r = await confirmCertGroup(pendingConfirmGroupId.value, campDate.value);
  if (r.error) { toast("确认失败: " + r.error); confirming.value = false; return; }
  toast("✅ 已确认成组，开营日: " + campDate.value);
  showCampDatePicker.value = false;
  await Promise.all([loadGroups(), loadMyCertGroupsData()]);
  openGroupDetail(pendingConfirmGroupId.value);
  // Trigger checklist status refresh after confirming group
  fetchChecklistStatus(pendingConfirmGroupId.value);
  confirming.value = false;
}
function groupStatusText(st: string) {
  if (st === "active") return "进行中";
  if (st === "recruiting") return "招募中";
  if (st === "completed") return "已完成";
  if (st === "disbanded") return "已解散";
  return "已完成";
}
function groupStatusBadgeClass(st: string) {
  if (st === "active") return "badge-active";
  if (st === "recruiting") return "badge-recruiting";
  if (st === "disbanded") return "badge-ended";
  return "badge-ended";
}

async function doConfirmCertGroup(id: number) {
  try {
    const r = await confirmCertGroup(id);
    if (r.error) { toast(r.error); return; }
    toast("✅ 已确认成组，开始学习！");
    closeAllPanels();
    await loadGroups();
    openGroupDetail(id);
  } catch { toast("操作失败"); }
}

async function doCompleteCertGroup(id: number) {
  openCompleteDialog(id);
}

// Create form
const createLevel = ref("");
const createStage = ref("");
const createDuration = ref(0);
const createRhythm = ref("");
const createMaxMembers = ref(6);
const createDesc = ref("");
const createTimeSlot = ref("灵活不固定");
const createCohort = ref("");

const createEffectiveStage = computed(() => {
  return createLevel.value === "初级" ? "考核冲刺" : createStage.value;
});
const createMinMembers = computed(() => {
  return createEffectiveStage.value === "微习惯设计深化" ? 5 : 3;
});
const createMemberOptions = computed(() => {
  const min = createMinMembers.value;
  const opts: number[] = [];
  for (let i = min; i <= 8; i++) opts.push(i);
  return opts;
});

// 阶段切换时自动调整人数下限
watch(createMinMembers, (min) => {
  if (createMaxMembers.value < min) createMaxMembers.value = min;
});

// 当打开小组详情时，如果是进行中的小组，加载清单追踪数据
watch(() => detail.value?.id, (newId) => {
  if (newId && detail.value?.status === 'active' && detail.value?.campStartDate) {
    fetchChecklistStatus(newId);
  } else {
    checklistStatus.value = null;
  }
});

// ═══════════════ Computed ═══════════════
const primaryGroups = computed(() => allGroups.value.filter((g: any) => g.certLevel === "初级"));
const level2Stages = [
  { key: "觉察觉照深化", name: "阶段一：觉察觉照深化", shortName: "觉察觉照" },
  { key: "倾听与提问", name: "阶段二：倾听与提问", shortName: "倾听与提问" },
  { key: "微习惯设计深化", name: "阶段三：微习惯设计深化", shortName: "微习惯设计" },
];
function getStageGroups(stage: string) {
  return allGroups.value.filter((g: any) => g.certLevel === "二级" && g.certStage === stage);
}

// Filtered level 2 groups
const filteredL2Groups = computed(() => {
  let l2 = allGroups.value.filter((g: any) => g.certLevel === "二级");
  if (l2Filter.value !== 'all') l2 = l2.filter((g: any) => g.certStage === l2Filter.value);
  if (cohortFilter.value !== 'all') l2 = l2.filter((g: any) => g.cohort === cohortFilter.value);
  return l2;
});

// Available cohorts from current level 2 groups
const availableCohorts = computed(() => {
  const cohorts = new Set<string>();
  allGroups.value
    .filter((g: any) => g.certLevel === "二级" && g.cohort)
    .forEach((g: any) => cohorts.add(g.cohort));
  return Array.from(cohorts).sort();
});

const applyStages = computed(() => {
  const stages = [{ key: "初级:考核冲刺", label: "初级 · 考核冲刺" }];
  for (const s of level2Stages) {
    stages.push({ key: `二级:${s.key}`, label: `二级 · ${s.name.slice(4)}` });
  }
  return stages;
});

const isApprovedLeader = computed(() =>
  myApplications.value.some((a: any) => a.status === "approved")
);

function isMyGroupCheck(d: any): boolean {
  if (!d || !d.members) return false;
  return d.members.some((m: any) => m.name === userName);
}
const isMyGroup = computed(() => isMyGroupCheck(detail.value));

// ═══════════════ Helpers ═══════════════
function rhythmClass(rhythm: string) {
  if (!rhythm) return "tag-rhythm";
  if (rhythm.includes("刚开始") || rhythm.includes("从头")) return "tag-rhythm";
  if (rhythm.includes("进行") || rhythm.includes("冲刺")) return "tag-period";
  return "tag-leader";
}
function statusLabel(s: string) {
  return s === "pending" ? "审核中" : s === "approved" ? "已通过" : s === "rejected" ? "未通过" : s;
}
function statusBadgeClass(s: string) {
  return s === "approved" ? "status-approved" : s === "rejected" ? "status-rejected" : "status-pending";
}
function toast(msg: string) {
  toastMsg.value = msg;
  setTimeout(() => { toastMsg.value = ""; }, 2200);
}
async function copyWechat(wx: string) {
  try {
    await navigator.clipboard.writeText(wx);
    toast("微信号已复制 ✅");
  } catch {
    toast("复制失败，请手动复制");
  }
}

// ═══════════════ Panel Management ═══════════════
function closeAllPanels() {
  showRecruitPanel.value = false;
  showDetailPanel.value = false;
  showCreatePanel.value = false;
  showApplyForm.value = false;
}
function openRecruitDetail() {
  showRecruitPanel.value = true;
  showApplyForm.value = false;
}
function openGroupDetail(id: number) { loadDetail(id); }
function openCreatePanel() { showCreatePanel.value = true; }

// ═══════════════ Data Loading ═══════════════
async function loadRecruitConfig() {
  try {
    const r = await fetchRecruitConfig();
    recruitConfig.value = r;
  } catch { /* ignore */ }
}

async function loadMyApplications() {
  try {
    const r = await fetchMyLeaderStatus();
    myApplications.value = r.applications || [];
  } catch { /* ignore */ }
}

async function loadGroups() {
  try {
    const r = await fetchCertGroups();
    allGroups.value = r.groups || [];
  } catch { /* ignore */ }
}

async function loadDetail(id: number) {
  try {
    const r = await fetchCertGroupDetail(id);
    if (r.error) { toast(r.error); return; }
    detail.value = r;
    showDetailPanel.value = true;
  } catch { toast("加载详情失败"); }
}

// ═══════════════ Actions ═══════════════
async function submitApply() {
  if (!applyStage.value) { toast("请先选择认证阶段"); return; }

  applying.value = true;
  try {
    const [certLevel, certStage] = applyStage.value.split(":");
    const r = await applyForLeader(certLevel!, certStage!, applyMessage.value || undefined);
    if (r.error) { toast(r.error); return; }
    toast("📩 申请已提交！");
    showApplyForm.value = false;
    applyStage.value = "";
    applyMessage.value = "";
    await loadMyApplications();
  } catch { toast("提交失败"); }
  finally { applying.value = false; }
}

// Edit/Withdraw application
const editingAppId = ref(0);
const editStage = ref("");
const editMessage = ref("");

function startEditApplication(app: any) {
  editingAppId.value = app.id;
  editStage.value = `${app.certLevel}:${app.certStage}`;
  editMessage.value = app.message || "";
  showApplyForm.value = false;
  showRecruitPanel.value = true;
}

function cancelEditApplication() {
  editingAppId.value = 0;
  editStage.value = "";
  editMessage.value = "";
}

async function saveEditApplication() {
  if (!editStage.value) { toast("请选择认证阶段"); return; }
  try {
    const [certLevel, certStage] = editStage.value.split(":");
    const patch: { certLevel?: string; certStage?: string; message?: string } = {};
    if (certLevel) patch.certLevel = certLevel;
    if (certStage) patch.certStage = certStage;
    if (editMessage.value) patch.message = editMessage.value;
    const r = await editLeaderApplication(editingAppId.value, patch);
    if (r.error) { toast(r.error); return; }
    toast("✅ 申请已更新");
    cancelEditApplication();
    await loadMyApplications();
  } catch { toast("编辑失败"); }
}

async function confirmWithdraw(id: number) {
  if (!confirm("确定要撤回这个申请吗？")) return;
  try {
    const r = await withdrawLeaderApplication(id);
    if (r.error) { toast(r.error); return; }
    toast("已撤回申请");
    await loadMyApplications();
  } catch { toast("撤回失败"); }
}

async function joinGroup(id: number) {
  try {
    const r = await joinCertGroup(id);
    if (r.error) { toast(r.error); return; }
    toast("✅ 已加入！");
    closeAllPanels();
    await loadGroups();
  } catch { toast("加入失败"); }
}

async function leaveGroup(id: number) {
  if (!confirm("确定要退出这个小组吗？")) return;
  try {
    const r = await leaveCertGroup(id);
    if (r.error) { toast(r.error); return; }
    toast("已退出小组");
    closeAllPanels();
    await loadGroups();
  } catch { toast("退出失败"); }
}

async function disbandGroup(id: number) {
  if (!confirm("确定要解散这个小组吗？此操作不可撤销。")) return;
  try {
    const r = await disbandCertGroup(id);
    if (r.error) { toast(r.error); return; }
    toast("小组已解散");
    closeAllPanels();
    await loadGroups();
  } catch { toast("解散失败"); }
}

async function doCreateGroup() {
  const certLevel = createLevel.value;
  let certStage = certLevel === "初级" ? "考核冲刺" : createStage.value;

  if (!certLevel || !certStage) { toast("请选择认证等级和主题"); return; }
  if (certLevel === "二级" && !createCohort.value) { toast("请选择认证期数"); return; }
  if (certLevel !== "二级" && !createDuration.value) { toast("请选择周期"); return; }

  creating.value = true;
  try {
    const r = await createCertGroup({
      certLevel,
      certStage,
      durationWeeks: certLevel === "二级" ? 4 : createDuration.value,
      rhythmDesc: certLevel === "二级" ? "" : createRhythm.value,
      maxMembers: createMaxMembers.value,
      description: createDesc.value,
      timeSlot: createTimeSlot.value,
      ...(createCohort.value ? { cohort: createCohort.value } : {})
    });
    if (r.error) { toast(r.error); return; }
    toast("🎉 认证小组已创建！");
    closeAllPanels();
    createLevel.value = "";
    createStage.value = "";
    createDuration.value = 0;
    createRhythm.value = "";
    createDesc.value = "";
    createCohort.value = "";
    await Promise.all([loadGroups(), loadMyCertGroupsData()]);
  } catch { toast("创建失败"); }
  finally { creating.value = false; }
}

function switchTab(tab: string) {
  activeTab.value = tab;
  if (tab === 'mine') loadMyCertGroupsData();
}

function logout() {
  localStorage.removeItem("rc_token");
  localStorage.removeItem("rc_user");
  router.replace({ name: "login" });
}

// ═══════════════ My Cert Groups ═══════════════
const myCertGroups = ref<any[]>([]);
const loadingMine = ref(false);

async function loadMyCertGroupsData() {
  loadingMine.value = true;
  try {
    const r = await fetchMyCertGroups();
    myCertGroups.value = r.groups || [];
  } catch { /* ignore */ }
  finally { loadingMine.value = false; }
}

async function leaveFromMine(id: number) {
  if (!confirm("确定要退出这个认证小组吗？")) return;
  try {
    const r = await leaveCertGroup(id);
    if (r.error) { toast(r.error); return; }
    toast("已退出");
    await Promise.all([loadGroups(), loadMyCertGroupsData()]);
  } catch { toast("退出失败"); }
}

async function disbandFromMine(id: number) {
  if (!confirm("确定要解散这个认证小组吗？此操作不可撤销。")) return;
  try {
    const r = await disbandCertGroup(id);
    if (r.error) { toast(r.error); return; }
    toast("已解散");
    await Promise.all([loadGroups(), loadMyCertGroupsData()]);
  } catch { toast("解散失败"); }
}

// ═══════════════ Init ═══════════════
onMounted(async () => {
  await Promise.all([loadRecruitConfig(), loadMyApplications(), loadGroups()]);
});
</script>

<style scoped>
/* ===== Design tokens ===== */
:root {
  --primary: #e8894f; --primary-light: #fef5ee; --primary-dark: #c96d35;
}
* { margin: 0; padding: 0; box-sizing: border-box; }

.cert-app {
  width: 100%; max-width: 430px; height: 100%;
  background: #fef9f2;  overflow: hidden;
  box-shadow: 0 12px 40px rgba(61,53,41,0.14);
  margin: 0 auto; position: relative; display: flex; flex-direction: column; overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
  color: #3d3529;
}

/* Header */
.page-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; background: #fff;
  border-bottom: 1px solid #f0e8dd;
}
.brand { font-weight: 700; font-size: 16px; color: #e8894f; }
.header-right { display: flex; align-items: center; gap: 10px; }
.username { font-size: 13px; color: #8b7e6a; }
.logout-btn { padding: 4px 10px; font-size: 11px; border: 1px solid #e8e4df; border-radius: 6px; background: none; color: #8b7e6a; cursor: pointer; }
.page-subtitle { padding: 8px 16px; font-size: 13px; color: #8b7e6a; background: #fdfaf7; border-bottom: 1px solid #f0ede9; }

/* Banner */
.recruit-banner {
  margin: 14px 20px 0;
  background: linear-gradient(135deg, #fff7ed, #fef5ee);
  border: 2px solid #e8894f; border-radius: 14px;
  padding: 16px 18px; display: flex; align-items: center; gap: 14px;
  cursor: pointer; transition: all 0.2s;
}
.recruit-banner:active { transform: scale(0.98); }
.banner-icon { font-size: 36px; flex-shrink: 0; }
.banner-text { flex: 1; }
.banner-title { font-size: 15px; font-weight: 700; color: #c96d35; margin-bottom: 3px; }
.banner-desc { font-size: 12px; color: #8b7e6a; line-height: 1.4; }
.banner-arrow { font-size: 18px; color: #e8894f; }
.pulse-dot { width: 8px; height: 8px; border-radius: 50%; background: #d4786e; display: inline-block; margin-right: 6px; animation: pulse-dot 2s infinite; }
@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(1.5); }
}

/* Tab bar */
.tab-bar { display: flex; padding: 0 20px; gap: 0; border-bottom: 1px solid #f0e8dd; }
.tab-bar button {
  flex: 1; padding: 14px 0; border: none; background: none;
  font-size: 15px; font-weight: 600; color: #b8a99a; cursor: pointer;
  position: relative; transition: color 0.2s; font-family: inherit;
}
.tab-bar button.active { color: #c96d35; }
.tab-bar button.active::after {
  content: ''; position: absolute; bottom: 0; left: 20%; right: 20%;
  height: 3px; background: #e8894f; border-radius: 3px 3px 0 0;
}

/* Content */
.content { flex: 1; overflow-y: auto; padding: 14px 20px 100px; }

/* Section title */
.section-title { font-size: 14px; font-weight: 700; margin: 18px 0 10px; display: flex; align-items: center; gap: 8px; }

/* Cards */
.group-card {
  background: #fff; border-radius: 14px; padding: 16px 18px;
  margin-bottom: 10px; border: 1px solid #f0e8dd;
  cursor: pointer; transition: all 0.2s;
}
.group-card:active { border-color: #e8894f; transform: scale(0.98); }
.card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
.group-name { font-size: 16px; font-weight: 700; }
.member-badge { font-size: 12px; color: #8b7e6a; background: #fef9f2; padding: 4px 10px; border-radius: 12px; font-weight: 600; }
.member-badge.ended { background: #f5f0e8; color: #b8a99a; }
.card-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }

/* Tags */
.tag { font-size: 11px; padding: 3px 10px; border-radius: 10px; font-weight: 500; }
.tag-rhythm { background: #edf6f1; color: #3d7a5a; }
.tag-leader { background: #eef4fa; color: #4a6d9e; }
.tag-period { background: #fdf8ed; color: #9a7b2a; }
.tag-official { background: #f4f2fa; color: #8b7ec4; }
.tag-stage { background: #fef5ee; color: #c96d35; }

/* Filter bar */
.filter-bar { display: flex; gap: 8px; overflow-x: auto; padding-bottom: 4px; margin-bottom: 14px; scrollbar-width: none; }
.filter-bar::-webkit-scrollbar { display: none; }
.filter-chip {
  flex-shrink: 0; padding: 7px 14px; border-radius: 20px;
  border: 1px solid #f0e8dd; background: #fff;
  font-size: 13px; color: #8b7e6a; cursor: pointer;
  white-space: nowrap; transition: all 0.2s; font-family: inherit;
}
.filter-chip.active { background: #fef5ee; border-color: #e8894f; color: #c96d35; font-weight: 600; }

/* Empty */
.empty-stage {
  text-align: center; padding: 24px 16px; margin-bottom: 10px;
  border: 1.5px dashed #f0e8dd; border-radius: 14px;
  color: #b8a99a; font-size: 13px; line-height: 1.6; cursor: pointer;
}
.empty-stage:active { border-color: #e8894f; background: #fef5ee; color: #c96d35; }
.empty-icon { font-size: 28px; display: block; margin-bottom: 6px; }

/* Info box */
.info-box {
  font-size: 13px; color: #8b7e6a; line-height: 1.8;
  background: #fff; padding: 14px 16px; border-radius: 8px;
  border: 1px solid #f0e8dd; margin-top: 20px;
}

/* Buttons */
.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 14px 24px; border-radius: 26px; font-size: 15px; font-weight: 600;
  cursor: pointer; border: none; transition: all 0.2s ease; font-family: inherit; width: 100%;
}
.btn:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
.btn:active { transform: translateY(0); box-shadow: none; }
.btn-cert { background: linear-gradient(135deg, #e8894f, #d4a843); color: #fff; }
.btn-cert:active { opacity: 0.85; transform: scale(0.98); }
.btn-cert:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-outline { background: #fff; color: #c96d35; border: 1.5px solid #e8894f; }
.btn-outline:active { background: #fef5ee; }
.btn-sm { padding: 8px 16px; font-size: 13px; width: auto; }

/* Overlay */
.overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.35); z-index: 50; display: none; }
.overlay.show { display: block; }

/* Panel */
.panel {
  position: fixed; bottom: 0; left: 50%; transform: translateX(-50%) translateY(100%);
  width: 100%; max-width: 430px; max-height: 85vh;
  background: #fef9f2; border-radius: 22px 22px 0 0; z-index: 60;
  display: flex; flex-direction: column;
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1); overflow: hidden;
}
.panel.show { transform: translateX(-50%) translateY(0); }
.panel-handle { width: 36px; height: 4px; background: #d4c8b8; border-radius: 2px; margin: 12px auto 8px; flex-shrink: 0; }
.panel-header { padding: 4px 20px 14px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #f0e8dd; flex-shrink: 0; }
.panel-header h3 { font-size: 18px; font-weight: 700; }
.panel-close { width: 32px; height: 32px; border-radius: 50%; border: none; background: #fff; color: #8b7e6a; font-size: 18px; cursor: pointer; }
.panel-body { flex: 1; overflow-y: auto; padding: 16px 20px 32px; }

/* Recruit detail */
.recruit-detail-header { text-align: center; padding: 8px 0 16px; }
.recruit-detail-header .big-icon { font-size: 48px; margin-bottom: 8px; }
.recruit-detail-header h2 { font-size: 19px; font-weight: 700; margin-bottom: 4px; }
.deadline { font-size: 13px; color: #d4786e; font-weight: 600; }

.recruit-card {
  background: #fff; border-radius: 14px; padding: 16px 18px;
  margin-bottom: 10px; border: 1px solid #f0e8dd;
}
.recruit-card h4 { font-size: 14px; font-weight: 700; margin-bottom: 6px; }
.info-row { font-size: 13px; color: #8b7e6a; line-height: 1.8; }
.info-row span { color: #3d3529; font-weight: 600; }

/* Status badges */
.status-pending { color: #d4a843; font-weight: 600; }
.status-approved { color: #3d7a5a; font-weight: 600; }
.status-rejected { color: #d4786e; font-weight: 600; }

/* Apply form */
.stage-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.stage-chip {
  padding: 8px 14px; border-radius: 20px; border: 1.5px solid #f0e8dd;
  background: #fff; font-size: 13px; cursor: pointer; color: #8b7e6a;
  transition: all 0.2s;
}
.stage-chip.selected { border-color: #e8894f; background: #fef5ee; color: #c96d35; font-weight: 600; }
.hint { font-size: 12px; color: #b8a99a; margin-top: 4px; }

/* Form */
.form-group { margin-bottom: 16px; }
.form-group label { display: block; font-size: 14px; font-weight: 600; margin-bottom: 6px; }
.form-group select, .form-group input, .form-group textarea {
  width: 100%; padding: 13px 16px; border: 1px solid #f0e8dd;
  border-radius: 8px; font-size: 15px; font-family: inherit;
  color: #3d3529; background: #fef9f2; transition: border-color 0.2s;
}
.form-group textarea { resize: vertical; min-height: 80px; }
.form-group select:focus, .form-group input:focus, .form-group textarea:focus {
  outline: none; border-color: #e8894f; box-shadow: 0 0 0 3px rgba(192,131,90,0.1);
  background: #fff;
}
.hint-text { font-size: 12px; color: #b8a99a; margin-top: 4px; }

/* Member row */
.member-row { display: flex; align-items: center; gap: 8px; padding: 4px 0; }
.member-avatar {
  width: 28px; height: 28px; border-radius: 50%;
  background: linear-gradient(135deg, #f0c8a0, #e8b080);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 12px; color: #fff; font-weight: 700; flex-shrink: 0;
}
.wechat-info { font-size: 11px; color: #b8a99a; margin-left: auto; display: flex; align-items: center; gap: 4px; }
.copy-btn { background: none; border: 1px solid #f0e8dd; border-radius: 6px; padding: 2px 6px; cursor: pointer; font-size: 12px; }

/* FAB */
.fab {
  position: fixed; bottom: calc(78px + env(safe-area-inset-bottom, 0px)); right: calc(50% - 215px + 20px);
  display: flex; align-items: center; gap: 6px; padding: 12px 20px;
  background: linear-gradient(135deg, #e8894f, #d4a843);
  color: #fff; border: none; border-radius: 24px;
  font-size: 14px; font-weight: 600; cursor: pointer;
  box-shadow: 0 4px 16px rgba(232,137,79,0.35); z-index: 40;
  transition: all 0.2s;
}
.fab:active { transform: scale(0.95); }
.fab-icon { font-size: 20px; line-height: 1; }
.fab-text { font-size: 14px; }

/* SOP button */
.sop-btn {
  position: relative; width: 28px; height: 28px; border-radius: 50%;
  border: 1.5px solid #e8894f; background: #fff; color: #c96d35;
  font-size: 14px; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s;
}
.sop-btn:active { background: #fef5ee; }
.sop-dot {
  position: absolute; top: -2px; right: -2px;
  width: 8px; height: 8px; border-radius: 50%;
  background: #d4786e; display: block;
}

/* Group status badges */
.badge-active { background: #edf6f1; color: #3d7a5a; }
.badge-recruiting { background: #fef5ee; color: #e8894f; }
.badge-ended { background: #f5f0e8; color: #b8a99a; }

/* Toast */
.toast {
  position: fixed; bottom: 60px; left: 50%; transform: translateX(-50%);
  background: #3d3529; color: #fff; padding: 12px 24px; border-radius: 24px;
  font-size: 14px; font-weight: 600; z-index: 100; opacity: 0;
  transition: opacity 0.3s; pointer-events: none;
}
.toast.show { opacity: 1; }

/* Dialog (date picker popup) */
.dialog {
  position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%);
  background: #fff; border-radius: 16px; padding: 24px;
  width: calc(100% - 64px); max-width: 360px; z-index: 70;
  box-shadow: 0 8px 30px rgba(0,0,0,0.15);
}
.btn-cancel { padding: 10px; border: 1px solid #e8e4df; border-radius: 10px; background: #fff; color: #8b7e6a; font-size: 14px; cursor: pointer; }
.btn-save { padding: 10px; border: none; border-radius: 10px; background: #e8894f; color: #fff; font-size: 14px; font-weight: 600; cursor: pointer; }
.chip { padding: 6px 14px; border: 1px solid #e8e4df; border-radius: 16px; background: #fafaf8; font-size: 13px; color: #4a4744; cursor: pointer; white-space: nowrap; }
.chip.active { background: #e8894f; color: #fff; border-color: #e8894f; }
</style>
